/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionGoBack';

const ActionGoBackManager: ActionManagerFn<'ActionGoBack'> = (
  platform: Platform,
): ActionManager<'ActionGoBack'> => {
  return {
    ...baseActionManager(platform, 'ActionGoBack'),
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Go Back',
    icon: { iconName: 'arrow-to-left' },
    description: `**Overview**<br/>
The Go Back action is a navigation component designed to return the user to the previously visited page within the application's history stack. It functionally mimics the behavior of a standard web browser's "Back" button, allowing for programmatic control over backward navigation.<br/><br/>
This component is structurally simple, operating without specific configuration parameters. It relies on the underlying platform or browser history mechanism to determine the destination, providing a seamless way to reverse the last navigation event.

**Key Features**<br/>
* **Browser History Integration:** Explicitly described as being "similar to clicking the browser back button," indicating it interacts with the history stack rather than a specific hardcoded destination.
* **Zero-Configuration:** Built directly on baseActionManager and baseActionProps, requiring no additional inputs to function.
* **Conditional Execution:** The description notes "if any," implying it handles scenarios where there is no previous page (e.g., a new tab) gracefully, likely by remaining on the current page.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Cancel Operations:** Assigned to a "Cancel" button on a form to discard the current view and return the user to the previous list or dashboard.
* **Hierarchical Navigation:** Allowing users to return to a parent category after viewing a specific detail page.`,
  };
};

export default ActionGoBackManager;
